import React, {Component} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import ProgressSpinner from "components/ProgressSpinner";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from "@material-ui/core";
import {BookingIcon} from "components/Icon";
import {LandlordBooking} from "services/entities/booking";
import {formatUserName} from "util/users";
import {loadBookingInvoice} from "services/bookings";
import {loadLandlordBookings} from "services/bookings/landlords";
import {DateTime} from "util/time";
import './index.scss';
import {downloadFile} from "util/file";
import {handleError} from "util/errors";
import {PagedResult} from "services/entities/queries";

interface BookingsTableState {
  bookings: PagedResult<LandlordBooking>|null;
  downloadingInvoiceBookings: string[];
}

interface BookingsTableProps extends RouteComponentProps {
}

class BookingsTable extends Component<BookingsTableProps, BookingsTableState> {
  private static readonly BOOKINGS_PER_PAGE = 20;
  state: BookingsTableState = {
    bookings: null,
    downloadingInvoiceBookings: []
  };

  componentDidMount(): void {
    this.loadBookings(0);
  }

  render() {
    let bookings = this.state.bookings;
    if(bookings == null) {
      return <div className="bookings-table loading"><ProgressSpinner/></div>;
    } else if(bookings.totalCount === 0) {
      return <div className="bookings-table empty">
        <BookingIcon/>
        <p>No bookings found.</p>
      </div>;
    } else {
      return this.renderBookingsTable(bookings);
    }
  }

  renderBookingsTable(bookings: PagedResult<LandlordBooking>) {
    const bookingsPerPage = BookingsTable.BOOKINGS_PER_PAGE;
    const downloadingInvoices = this.state.downloadingInvoiceBookings;
    return <div className="bookings-table">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Occupation Date</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Booking Date</TableCell>
              <TableCell>Price (Ex VAT)</TableCell>
              <TableCell>VAT</TableCell>
              <TableCell>Total Price</TableCell>
              <TableCell>Landlord Split</TableCell>
              <TableCell>Sook Split</TableCell>
              <TableCell/>
              <TableCell/>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.results.map((b, i) =>
              <TableRow key={i}>
                <TableCell>{b.startTime.format("ddd DD MMM")}</TableCell>
                <TableCell>{formatUserName(b.creator)}</TableCell>
                <TableCell>{b.created.format("ddd DD MMM")}</TableCell>
                <TableCell>{b.price.format("$0.00")}</TableCell>
                <TableCell>{b.tax.format("$0.00")}</TableCell>
                <TableCell>{b.totalPrice.format("$0.00")}</TableCell>
                <TableCell>{b.landlordSplit.format("$0.00")}</TableCell>
                <TableCell>{b.sookSplit.format("$0.00")}</TableCell>
                <TableCell className="invoice">
                  <Button className="download-invoice" onClick={() => this.onBookingInvoiceClick(b)} disabled={downloadingInvoices.indexOf(b.code) >= 0}>
                    { downloadingInvoices.indexOf(b.code) < 0 ? 'Invoice' : <ProgressSpinner /> }
                  </Button>
                </TableCell>
                <TableCell>{ BookingsTable.getBookingPaymentStatus(b) }</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        className="bookings-pagination"
        count={bookings.totalCount}
        rowsPerPageOptions={[bookingsPerPage]}
        rowsPerPage={bookingsPerPage}
        page={bookings.pageIndex}
        labelDisplayedRows={(paginationInfo) => `Showing ${paginationInfo.from} - ${paginationInfo.to} of ${paginationInfo.count}`}
        onChangePage={this.onBookingsPageChanged}
      />
    </div>;
  }

  static getBookingPaymentStatus(booking: LandlordBooking): string {
    if(booking.outstandingAmount === null || booking.outstandingAmount.amount === 0) {
      return 'Paid';
    } else if(booking.outstandingAmount.amount >= booking.totalPrice.amount) {
      return 'Awaiting Payment';
    } else {
      return 'Partial Payment';
    }
  }


  loadBookings(page: number) {
    loadLandlordBookings({maxTime: DateTime.now().endOf('day')}, { pageIndex: page, pageSize: BookingsTable.BOOKINGS_PER_PAGE }).then(
      bookings => this.setState({bookings: bookings})
    );
  }

  onBookingsPageChanged = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    this.loadBookings(page);
  };

  onBookingInvoiceClick = (booking: LandlordBooking) => {
    this.setBookingInvoiceDownloading(booking, true);
    loadBookingInvoice(booking.code).then(
      invoice => {
        this.setBookingInvoiceDownloading(booking, false);
        const fileExt = invoice.type === 'application/zip' ? 'zip' : 'pdf';
        downloadFile(URL.createObjectURL(invoice), `booking_invoice_${booking.code}.${fileExt}`);
      },
      ex => {
        this.setBookingInvoiceDownloading(booking, false);
        handleError('Failed to download invoice', ex);
      }
    );
  };

  setBookingInvoiceDownloading(booking: LandlordBooking, downloading: boolean) {
    const downloadingInvoices = this.state.downloadingInvoiceBookings;
    const index = downloadingInvoices.indexOf(booking.code);
    if(downloading) {
      if (index < 0) {
        downloadingInvoices.push(booking.code);
        this.setState({downloadingInvoiceBookings: downloadingInvoices});
      }
    } else if (index >= 0) {
      downloadingInvoices.splice(index, 1);
      this.setState({downloadingInvoiceBookings: downloadingInvoices});
    }
  }
}

export default withRouter(BookingsTable);